import { cn } from '../cn';
import { useSmoothed } from '../hooks/useSmoothed';

/** 4 px track with a fill; red above 85 %. Fleet "Audio" column. */
export function BarMeter({ value, max = 100, className, danger = 85 }: { value: number; max?: number; className?: string; danger?: number }) {
  const shown = useSmoothed(value, 80, 500);
  const pct = Math.max(0, Math.min(100, (shown / max) * 100));
  return (
    <div className={cn('h-1 max-w-[96px] overflow-hidden rounded-[2px] bg-track', className)} role="meter" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}>
      <div className={cn('h-full', pct > danger ? 'bg-bad' : 'bg-ok')} style={{ width: `${pct}%` }} />
    </div>
  );
}

export interface SegmentMeterProps {
  /** Level in dBFS (−60 … 0). */
  dbfs: number;
  segments?: number;
  className?: string;
  /** Height 14 px desktop / 12 px mobile by default. */
  height?: number;
}

/** 28-LED input meter: thresholds −56 + 2·i dB; i ≥ 25 red, i ≥ 21 amber, else green; off = track. */
export function SegmentMeter({ dbfs, segments = 28, className, height }: SegmentMeterProps) {
  const shown = useSmoothed(dbfs, 60, 450); // cloud samples arrive every 250 ms at best: glide, do not step
  return (
    <div className={cn('flex h-3 items-end gap-[2px] md:h-3.5', className)} style={height ? { height } : undefined} role="meter" aria-valuenow={Math.round(dbfs)} aria-valuemin={-60} aria-valuemax={0} aria-label="Input level">
      {Array.from({ length: segments }, (_, i) => {
        const thr = -56 + i * 2;
        const on = shown >= thr;
        return <span key={i} className={cn('h-full flex-1 rounded-[1px]', on ? (i >= 25 ? 'bg-bad' : i >= 21 ? 'bg-warn' : 'bg-ok') : 'bg-track')} />;
      })}
    </div>
  );
}

/** 3 px progress: accent while running, ok at 100 %. */
export function ProgressBar({ pct, className }: { pct: number; className?: string }) {
  const p = Math.max(0, Math.min(100, pct));
  return (
    <div className={cn('h-[3px] overflow-hidden rounded-[2px] bg-track', className)} role="progressbar" aria-valuenow={Math.round(p)} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn('h-full transition-width duration-100 ease-linear', p >= 100 ? 'bg-ok' : 'bg-accent')} style={{ width: `${p}%` }} />
    </div>
  );
}
